import { useCallback, useEffect, useMemo, useState } from "react";
import {
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import { SelectChangeEvent } from "@mui/material/Select";
import { User } from "../../../../shared/types/user";
import { deleteUser, getMe, getUsers, postUser, updateUser } from "../../../../features/search/service/user-service";
import { getCompaniesListSelect } from "../../../../features/search/service/company-service";
import { getNonSuperAdminRoles, getRoleById, getRoles } from "../../../../features/search/service/role-service";
import { SelectOption } from "../../../../shared/types/select-options";
import { MenuTop } from "../../../../components/menu/top/menu-top";
import {
    AdminCrudAlerts,
    AdminCrudDeleteDialog,
    AdminCrudFormDialog,
    AdminCrudHeader,
    AdminCrudSearch
} from "../../../../components/admin-crud/admin-crud";
import { ROLES } from "../../../../shared/consts/roles";
import { useTranslation } from "../../../../shared/i18n/i18n-context";
import { ProjectLoader } from "../../../../components/project-loader/project-loader";
import "./styles/list.css";

const SUPER_ADMIN_ROLE = "super-admin";

type UserFormState = {
    name: string;
    email: string;
    password: string;
    roleId: string;
    companyId: string;
};

const emptyForm: UserFormState = {
    name: "",
    email: "",
    password: "",
    roleId: "",
    companyId: ""
};

const getRoleLabel = (user: User, fallback: string) => {
    return user.role?.name || user.role?.description || fallback;
};

const getCompanyLabel = (user: User, fallback: string) => {
    return user.company?.name || user.company?.description || fallback;
};

export const AdminUsersList = () => {
    const { t } = useTranslation('admin');
    const { t: menuT } = useTranslation('menu');
    const [users, setUsers] = useState<User[]>([]);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [roles, setRoles] = useState<SelectOption[]>([]);
    const [companyFilter, setCompanyFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [me, setMe] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [form, setForm] = useState<UserFormState>(emptyForm);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const isSuperAdmin = me?.role?.name === SUPER_ADMIN_ROLE || me?.role?.description === ROLES.SUPER_ADMIN;
    const adminRootPath = isSuperAdmin ? '/admin-painel-causale' : '/admin-painel';
    const adminRootLabel = isSuperAdmin ? t('causalePanel') : t('adminPanel');

    const fetchUsers = useCallback(async (selectedCompanyId = companyFilter, userIsSuperAdmin = isSuperAdmin) => {
        const usersData = await getUsers(userIsSuperAdmin ? selectedCompanyId || undefined : undefined);
        setUsers(usersData);
    }, [companyFilter, isSuperAdmin]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const userData = await getMe();
            const roleData = userData.role || (userData.role_id ? await getRoleById(userData.role_id) : null);
            const normalizedMe: User = roleData ? { ...userData, role: roleData } : userData;
            const userIsSuperAdmin = roleData?.name === SUPER_ADMIN_ROLE || roleData?.description === ROLES.SUPER_ADMIN;

            const [usersData, rolesData, companiesData] = await Promise.all([
                getUsers(userIsSuperAdmin ? companyFilter || undefined : undefined),
                userIsSuperAdmin ? getRoles() : getNonSuperAdminRoles(),
                userIsSuperAdmin ? getCompaniesListSelect() : Promise.resolve(userData.companyId ? [{
                    id: userData.companyId,
                    label: userData.company?.name || t('myCompany')
                }] : [])
            ]);

            setMe(normalizedMe);
            setUsers(usersData);
            setRoles(rolesData);
            setCompanies(companiesData);
        } catch (err: any) {
            setError(err.message || t('usersLoadError'));
        } finally {
            setLoading(false);
        }
    }, [companyFilter, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filteredUsers = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        if (!normalizedSearch) {
            return users;
        }

        return users.filter((user) => {
            const searchableText = [
                user.name,
                user.email,
                getRoleLabel(user, t('noRole')),
                user.role_id,
                getCompanyLabel(user, t('noCompany'))
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return searchableText.includes(normalizedSearch);
        });
    }, [searchTerm, t, users]);

    const updateForm = (field: keyof UserFormState, value: string) => {
        setForm((current) => ({ ...current, [field]: value }));
    };

    const openAddForm = () => {
        setEditingUser(null);
        setForm({
            ...emptyForm,
            roleId: String(roles[0]?.id || ""),
            companyId: String(companies[0]?.id || "")
        });
        setFormOpen(true);
    };

    const openEditForm = (user: User) => {
        setEditingUser(user);
        setForm({
            name: user.name || "",
            email: user.email || "",
            password: "",
            roleId: String(user.role?.id || user.role_id || ""),
            companyId: String(user.company?.id || user.companyId || "")
        });
        setFormOpen(true);
    };

    const closeForm = () => {
        if (saving) return;
        setFormOpen(false);
        setEditingUser(null);
        setForm(emptyForm);
    };

    const handleCompanyFilterChange = async (event: SelectChangeEvent) => {
        const selectedCompanyId = event.target.value;

        setCompanyFilter(selectedCompanyId);

        try {
            setLoading(true);
            setError("");
            const usersData = await getUsers(selectedCompanyId || undefined);
            setUsers(usersData);
        } catch (err: any) {
            setError(err.message || t('usersFilterError'));
        } finally {
            setLoading(false);
        }
    };

    const buildPayload = () => ({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        roleId: form.roleId,
        companyId: form.companyId || me?.companyId
    });

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setError(t('fillName'));
            return;
        }

        if (!form.email.trim()) {
            setError(t('fillEmail'));
            return;
        }

        if (!editingUser && !form.password.trim()) {
            setError(t('fillPassword'));
            return;
        }

        if (!form.roleId) {
            setError(t('selectAccessLevel'));
            return;
        }

        if (!form.companyId && !me?.companyId) {
            setError(t('selectCompany'));
            return;
        }

        try {
            setSaving(true);
            setError("");

            if (editingUser?.id) {
                const payload = buildPayload();
                delete (payload as Partial<typeof payload>).password;
                await updateUser(editingUser.id, payload as User);
                setSuccessMessage(t('userUpdated'));
            } else {
                await postUser(buildPayload() as User);
                setSuccessMessage(t('userAdded'));
            }

            closeForm();
            await fetchUsers();
        } catch (err: any) {
            setError(err.message || (editingUser ? t('updateUserError') : t('createUserError')));
        } finally {
            setSaving(false);
        }
    };

    const openDeleteDialog = (user: User) => {
        if (user.id === me?.id) {
            setError(t('cannotDeleteOwnAccount'));
            return;
        }

        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        if (deleting) return;
        setUserToDelete(null);
        setDeleteDialogOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete?.id) return;

        try {
            setDeleting(true);
            setError("");

            await deleteUser(userToDelete.id);
            setSuccessMessage(t('userRemoved'));
            closeDeleteDialog();
            await fetchUsers();
        } catch (err: any) {
            setError(err.message || t('userDeleteError'));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="admin-users-page">
            <MenuTop />
            <div className="users-container">
                <AdminCrudHeader
                    title={t('users')}
                    subtitle={`${filteredUsers.length} ${filteredUsers.length === 1 ? t('userSingular') : t('userPlural')} ${filteredUsers.length === 1 ? t('foundSingular') : t('foundPlural')}`}
                    addLabel={t('addUser')}
                    addIcon={<PersonAddIcon />}
                    onAdd={openAddForm}
                    breadcrumbItems={[
                        { label: menuT('home'), to: '/home' },
                        { label: adminRootLabel, to: adminRootPath },
                        { label: t('users') }
                    ]}
                />

                <div className="users-filters">
                    <AdminCrudSearch
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={t('searchUsers')}
                    />

                    {isSuperAdmin && (
                        <FormControl fullWidth size="small">
                            <InputLabel id="users-company-filter-label">{t('company')}</InputLabel>
                            <Select
                                labelId="users-company-filter-label"
                                value={companyFilter}
                                label={t('company')}
                                onChange={handleCompanyFilterChange}
                            >
                                <MenuItem value="">{t('allCompanies')}</MenuItem>
                                {companies.map((company) => (
                                    <MenuItem key={company.id} value={String(company.id)}>
                                        {company.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </div>

                <AdminCrudAlerts
                    error={error}
                    success={successMessage}
                    onClearError={() => setError("")}
                    onClearSuccess={() => setSuccessMessage("")}
                />

                {loading ? (
                    <div className="users-loading">
                        <ProjectLoader label={t('loadingUsers')} compact />
                    </div>
                ) : (
                    <TableContainer component={Paper} className="users-table-container">
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('name')}</TableCell>
                                    <TableCell>{t('email')}</TableCell>
                                    <TableCell>{t('role')}</TableCell>
                                    <TableCell>{t('company')}</TableCell>
                                    <TableCell align="center">{t('actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.map((user) => (
                                    <TableRow key={user.id} hover>
                                        <TableCell>
                                            <div className="users-name-cell">
                                                <PersonIcon />
                                                <strong>{user.name}</strong>
                                            </div>
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{getRoleLabel(user, t('noRole'))}</TableCell>
                                        <TableCell>{getCompanyLabel(user, t('noCompany'))}</TableCell>
                                        <TableCell align="center">
                                            <IconButton aria-label={t('editUser')} onClick={() => openEditForm(user)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                aria-label={t('deleteUser')}
                                                color="error"
                                                disabled={user.id === me?.id}
                                                onClick={() => openDeleteDialog(user)}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {filteredUsers.length === 0 && (
                            <div className="users-empty">{t('noUsersFound')}</div>
                        )}
                    </TableContainer>
                )}
            </div>

            <AdminCrudFormDialog
                open={formOpen}
                title={editingUser ? t('editUser') : t('addUser')}
                closeLabel={t('close')}
                cancelLabel={t('cancel')}
                saveLabel={saving ? t('saving') : t('save')}
                saving={saving}
                onClose={closeForm}
                onSave={handleSubmit}
            >
                <TextField
                    label={t('name')}
                    value={form.name}
                    onChange={(event) => updateForm("name", event.target.value)}
                    fullWidth
                    required
                />
                <TextField
                    label={t('email')}
                    value={form.email}
                    onChange={(event) => updateForm("email", event.target.value)}
                    fullWidth
                    required
                    type="email"
                />
                {!editingUser && (
                    <TextField
                        label={t('password')}
                        value={form.password}
                        onChange={(event) => updateForm("password", event.target.value)}
                        fullWidth
                        required
                        type="password"
                    />
                )}
                <FormControl fullWidth>
                    <InputLabel id="user-company-label">{t('company')}</InputLabel>
                    <Select
                        labelId="user-company-label"
                        label={t('company')}
                        value={form.companyId}
                        onChange={(event) => updateForm("companyId", event.target.value)}
                    >
                        {companies.map((company) => (
                            <MenuItem key={company.id} value={String(company.id)}>
                                {company.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <InputLabel id="user-role-label">{t('role')}</InputLabel>
                    <Select
                        labelId="user-role-label"
                        label={t('role')}
                        value={form.roleId}
                        onChange={(event) => updateForm("roleId", event.target.value)}
                    >
                        {roles.map((role) => (
                            <MenuItem key={role.id} value={String(role.id)}>
                                {role.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </AdminCrudFormDialog>

            <AdminCrudDeleteDialog
                open={deleteDialogOpen}
                title={t('deleteUser')}
                message={t('deleteUserMessage').replace('{name}', userToDelete?.name || '')}
                cancelLabel={t('cancel')}
                confirmLabel={deleting ? t('removing') : t('delete')}
                deleting={deleting}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
};
